'use client'

import * as PopoverPrimitive from '@radix-ui/react-popover'
import type { ComponentPropsWithoutRef, ComponentRef } from 'react'
import { forwardRef } from 'react'
import { cn } from '../lib/utils.ts'
import { usePortalTheme } from '../portal-theme.tsx'

/**
 * Non-modal floating panel anchored to a trigger, composed like upstream
 * shadcn: `Popover` › `PopoverTrigger` › `PopoverContent`.
 *
 * Distinct from `Dialog`, which owns the screen: a popover keeps the page
 * scrollable and the rest of it operable, which is what a date picker or a
 * filter menu wants. Radix supplies the outside-click / Escape / focus-return
 * model and the collision handling, so a panel near the viewport edge flips
 * side instead of being clipped.
 */
export const Popover = PopoverPrimitive.Root
export const PopoverTrigger = PopoverPrimitive.Trigger
export const PopoverAnchor = PopoverPrimitive.Anchor

export const PopoverContent = forwardRef<
  ComponentRef<typeof PopoverPrimitive.Content>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(function PopoverContent({ className, align = 'center', sideOffset = 4, ...props }, ref) {
  // Portalled onto <body>, outside whatever element carries the theme class —
  // stamp it back on as a CLASS, since the theme variant is
  // `&:where(.dark, .dark *)`. See `portal-theme.tsx`.
  const portalTheme = usePortalTheme()

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          portalTheme,
          // Same reasoning as `SelectContent`: the height cap takes part in
          // Radix's collision maths, so a tall panel scrolls inside the room it
          // was actually given rather than spilling off-screen.
          'z-50 max-h-[min(32rem,var(--radix-popover-content-available-height,32rem))] overflow-auto rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
})
PopoverContent.displayName = 'PopoverContent'
